import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { BreadcrumbNav } from '@/components/base/BreadcrumbNav';
import { animeList, categories } from '@/mocks/anime';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    const cat = categories.find((c) => c.slug === activeSlug);
    const catName = cat ? cat.name : '全部';
    document.title = `${catName}动漫 - 奶泡动漫高清在线观看 | 海量番剧分类`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', `奶泡动漫${catName}专区，海量${catName}番剧高清在线观看，每日更新最新话，画质清晰流畅，尽享极致追番体验。`);
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${window.location.origin}/category/${activeSlug}`);
    }
  }, [activeSlug]);

  const filteredAnime = useMemo(() => {
    if (activeSlug === 'all') return animeList;
    const cat = categories.find((c) => c.slug === activeSlug);
    if (!cat) return animeList;
    return animeList.filter(
      (a) =>
        a.categorySlug === activeSlug ||
        a.subCategory === cat.name ||
        a.tags.some((t) => t === cat.name)
    );
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const handleCategoryClick = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/category/${newSlug}`);
  };

  const handleAnimeClick = (id: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/article/${id}`);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${activeCategory?.name || '全部'}动漫 - 奶泡动漫`,
        url: `${window.location.origin}/category/${activeSlug}`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: window.location.origin + '/' },
            { '@type': 'ListItem', position: 2, name: activeCategory?.name || '全部' },
          ],
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <BreadcrumbNav
            items={[
              { label: '首页', href: '/' },
              { label: activeCategory?.name || '全部动漫' },
            ]}
          />

          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
            {activeCategory?.name || '全部'}动漫
          </h1>
          <p className="text-sm text-foreground-500 mb-8">
            共 {filteredAnime.length} 部番剧，海量高清动漫在线观看
          </p>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {filteredAnime.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-emotion-sad-line text-2xl text-primary-500"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的番剧，看看其他分类吧</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredAnime.map((anime) => (
                <div
                  key={anime.id}
                  onClick={() => handleAnimeClick(anime.id)}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                    <img
                      src={anime.coverImage}
                      alt={anime.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <div className="w-full flex items-center justify-between">
                        <span className="text-white text-xs bg-primary-500 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {anime.rating}分
                        </span>
                        <span className="text-white/80 text-xs whitespace-nowrap">
                          {anime.episodes}集
                        </span>
                      </div>
                    </div>
                    {anime.isNew && (
                      <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                        新番
                      </span>
                    )}
                    <span className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                      {anime.status}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {anime.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                    {anime.category} · {anime.subCategory} · {anime.year}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}